import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import useFetch from './useFetch.jsx';
import useApiRequest from './useApiRequest.jsx';
import { domain } from './Domain.jsx';
import { motion } from 'framer-motion';
import {
    Mail, Phone, MapPin, Clock, Facebook, Twitter, Instagram,
    Linkedin, Youtube, ArrowRight, Heart, Shield, Users,
    Calendar, Star, Zap, Globe, Award, TrendingUp
} from 'lucide-react';

const Footer = () => {
    const { data: categories, loading: categoriesLoading, error: categoriesError } = useFetch(`${domain}/event-categories`);
    const { data: stats, loading: statsLoading, error: statsError } = useFetch(`${domain}/statistics`);
    const [email, setEmail] = useState('');
    const [isSubscribed, setIsSubscribed] = useState(false);
    const [subscriptionError, setSubscriptionError] = useState('');
    const [subscriptionInfo, setSubscriptionInfo] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    const { postRequest } = useApiRequest();

    const handleNewsletterSubmit = async (e) => {
        e.preventDefault();
        if (!email.trim()) return;
        
        setIsSubmitting(true);
        setSubscriptionError('');
        setSubscriptionInfo('');
        
        try {
            const response = await postRequest(`${domain}/newsletter-subscriptions`, { email: email.trim() });
            
            if (response.status === 201 || response.status === 200) {
                // Success - new subscription or reactivation
                setIsSubscribed(true);
                setEmail('');
                setTimeout(() => setIsSubscribed(false), 5000);
            } else if (response.status === 400) {
                // Handle specific error cases
                const message = response.details?.error || response.error || response.message || 'Subscription failed. Please try again.';
                // Check if it's an "already subscribed" message - treat as info, not error
                if (message.toLowerCase().includes('already subscribed')) {
                    setSubscriptionInfo(message);
                    setSubscriptionError('');
                    setEmail('');
                    // Clear info message after 5 seconds
                    setTimeout(() => setSubscriptionInfo(''), 5000);
                } else {
                    setSubscriptionError(message);
                    setSubscriptionInfo('');
                }
            } else {
                setSubscriptionError(response.body?.details?.error || response.body?.error || 'Subscription failed. Please try again.');
            }
        } catch (error) {
            setSubscriptionError('An error occurred. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const socialLinks = [
        { icon: <Facebook className="w-5 h-5" />, href: "#", label: "Facebook", color: "hover:bg-blue-600" },
        { icon: <Twitter className="w-5 h-5" />, href: "#", label: "Twitter", color: "hover:bg-sky-500" },
        { icon: <Instagram className="w-5 h-5" />, href: "#", label: "Instagram", color: "hover:bg-pink-600" },
        { icon: <Linkedin className="w-5 h-5" />, href: "#", label: "LinkedIn", color: "hover:bg-blue-700" },
        { icon: <Youtube className="w-5 h-5" />, href: "#", label: "YouTube", color: "hover:bg-red-600" }
    ];

    const quickLinks = [
        { name: "How it works", href: "/how-it-works" },
        { name: "Help Center", href: "/help-center" },
        { name: "About Us", href: "/about" },
        { name: "Contact", href: "/contact" },
        { name: "Privacy Policy", href: "/privacy" },
        { name: "Terms of Service", href: "/terms" }
    ];

    const contactInfo = [
        { icon: <Mail className="w-4 h-4" />, text: "info@eventplots.com", href: "mailto:info@eventplots.com" },
        { icon: <Phone className="w-4 h-4" />, text: "+256 200 905 932", href: "tel:+256200905932" },
        { icon: <MapPin className="w-4 h-4" />, text: "Kampala, Uganda", href: "#" },
        { icon: <Clock className="w-4 h-4" />, text: "24/7 Support", href: "#" }
    ];

    // Dynamic stats from backend with fallback values
    const statsData = [
        {
            icon: <Users className="w-5 h-5" />,
            value: stats?.total_users ? (stats.total_users >= 1000 ? `${(stats.total_users / 1000).toFixed(1)}K+` : stats.total_users.toString()) : "50K+",
            label: "Happy Users",
            loading: statsLoading
        },
        {
            icon: <Calendar className="w-5 h-5" />,
            value: stats?.total_events ? (stats.total_events >= 1000 ? `${(stats.total_events / 1000).toFixed(1)}K+` : stats.total_events.toString()) : "10K+",
            label: "Events",
            loading: statsLoading
        },
        {
            icon: <Star className="w-5 h-5" />,
            value: stats?.average_rating ? `${stats.average_rating}★` : "4.8★",
            label: "Rating",
            loading: statsLoading
        },
        {
            icon: <TrendingUp className="w-5 h-5" />,
            value: stats?.uptime_percentage ? `${stats.uptime_percentage}%` : "99%",
            label: "Uptime",
            loading: statsLoading
        }
    ];

    return (
        <footer className="bg-gradient-to-br from-slate-900 via-gray-900 to-slate-800 text-white">
            {/* Main Footer Content */}
            <div className="container mx-auto px-4 py-16">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                    {/* Company Info */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="lg:col-span-1"
                    >
                        <div className="flex items-center gap-2 mb-4 md:mb-6">
                            <div className="w-8 h-8 md:w-10 md:h-10 bg-gradient-to-br from-orange-400 to-orange-500 rounded-xl flex items-center justify-center shadow-lg">
                                {/* <Globe className="w-4 h-4 md:w-6 md:h-6 text-white" /> */}
                                <img src="/images/logo.png" alt="EventPlots Logo" className="w-10 h-10 rounded-lg" />
                            </div>
                            <div className="flex flex-col">
                                <span className="text-lg md:text-xl font-bold">
                                    <span className="text-orange-400">Event</span>
                                    <span className="text-teal-400">Plots</span>
                                </span>
                                <span className="text-xs text-gray-300 -mt-1">Discover Amazing Events</span>
                            </div>
                        </div>
                        <p className="text-sm md:text-base text-gray-300 leading-relaxed mb-4 md:mb-6">
                            Uganda's premier ticketing platform, offering seamless event booking, management, and discovery.
                            Enjoy a user-friendly, secure, and reliable experience tailored to meet all your event needs.
                        </p>

                        {/* Social Media Links */}
                        <div className="flex gap-2 md:gap-3">
                            {socialLinks.map((social, index) => (
                                <motion.a
                                    key={index}
                                    href={social.href}
                                    aria-label={social.label}
                                    className="w-8 h-8 md:w-10 md:h-10 bg-gray-800 hover:bg-gradient-to-br hover:from-orange-400 hover:to-orange-500 rounded-lg flex items-center justify-center transition-all duration-300 hover:scale-110 hover:shadow-lg border border-gray-700"
                                    whileHover={{ scale: 1.1 }}
                                    whileTap={{ scale: 0.95 }}
                                >
                                    {social.icon}
                                </motion.a>
                            ))}
                        </div>
                    </motion.div>

                    {/* Quick Links */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                        style={{ color: "white" }}
                    >
                        <h4 className="text-lg md:text-xl font-bold text-white mb-4 md:mb-6 flex items-center gap-2">
                            <ArrowRight className="w-4 h-4 md:w-5 md:h-5 text-orange-400" />
                            Quick Links
                        </h4>
                        <ul className="space-y-2 md:space-y-3">
                            {quickLinks.map((link, index) => (
                                <li key={index}>
                                    <Link
                                        to={link.href}
                                        className="text-sm md:text-base text-gray-300 hover:text-orange-400 transition-colors duration-200 flex items-center gap-2 group"
                                    >
                                        <div className="w-1 h-1 bg-orange-400 rounded-full group-hover:scale-150 transition-transform duration-200"></div>
                                        {link.name}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </motion.div>

                    {/* Event Categories */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        style={{ color: "white" }}
                    >
                        <h4 className="text-lg md:text-xl font-bold text-white mb-4 md:mb-6 flex items-center gap-2">
                            <Calendar className="w-4 h-4 md:w-5 md:h-5 text-teal-400" />
                            Event Categories
                        </h4>
                        <ul className="space-y-2 md:space-y-3">
                            {categoriesLoading ? (
                                <div className="space-y-2">
                                    {[...Array(6)].map((_, i) => (
                                        <div key={i} className="h-3 md:h-4 bg-gray-800 rounded animate-pulse"></div>
                                    ))}
                                </div>
                            ) : categories && categories.slice(0, 6).map((category, index) => (
                                <li key={index}>
                                    <Link
                                        to={`/categories/${category.name.toLowerCase().replace(/\s+/g, '-')}`}
                                        className="text-sm md:text-base text-gray-300 hover:text-teal-400 transition-colors duration-200 flex items-center gap-2 group"
                                    >
                                        <div className="w-1 h-1 bg-teal-400 rounded-full group-hover:scale-150 transition-transform duration-200"></div>
                                        {category.name}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </motion.div>

                    {/* Newsletter & Contact */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.3 }}
                        style={{ color: "white" }}
                    >
                        <h4 className="text-lg md:text-xl font-bold text-white mb-4 md:mb-6 flex items-center gap-2">
                            <Mail className="w-4 h-4 md:w-5 md:h-5 text-orange-400" />
                            Stay Updated
                        </h4>

                        {/* Newsletter Signup */}
                        <div className="mb-4 md:mb-6">
                            <p className="text-gray-300 text-sm mb-3">
                                Subscribe to our newsletter for the latest events and updates.
                            </p>
                            <form onSubmit={handleNewsletterSubmit} className="space-y-3">
                                <div className="relative">
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="Enter your email"
                                        className="w-full px-3 py-2 md:px-4 md:py-3 bg-gray-800 border border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent text-white placeholder-gray-400 transition-all duration-200 text-sm"
                                        required
                                        disabled={isSubmitting}
                                    />
                                </div>
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="w-full bg-gradient-to-r from-teal-400 to-teal-500 hover:from-teal-500 hover:to-teal-600 text-white px-3 py-2 md:px-4 md:py-3 rounded-lg font-medium transition-all duration-200 hover:shadow-lg flex items-center justify-center gap-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isSubmitting ? (
                                        <>
                                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                            Subscribing...
                                        </>
                                    ) : (
                                        <>
                                            Subscribe
                                            <ArrowRight className="w-4 h-4" />
                                        </>
                                    )}
                                </button>
                            </form>
                            
                            {/* Success Message */}
                            {isSubscribed && (
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.8 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    className="mt-2 text-teal-400 text-sm flex items-center gap-1"
                                >
                                    <Shield className="w-4 h-4" />
                                    Successfully subscribed!
                                </motion.div>
                            )}
                            
                            {/* Info Message */}
                            {subscriptionInfo && (
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.8 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    className="mt-2 text-blue-400 text-sm flex items-center gap-1"
                                >
                                    <Shield className="w-4 h-4" />
                                    {subscriptionInfo}
                                </motion.div>
                            )}
                            
                            {/* Error Message */}
                            {subscriptionError && (
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.8 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    className="mt-2 text-red-400 text-sm flex items-center gap-1"
                                >
                                    <Shield className="w-4 h-4" />
                                    {subscriptionError}
                                </motion.div>
                            )}
                        </div>

                        {/* Contact Info */}
                        <div className="space-y-2 md:space-y-3">
                            {contactInfo.map((contact, index) => (
                                <a
                                    key={index}
                                    href={contact.href}
                                    className="flex items-center gap-2 md:gap-3 text-gray-300 hover:text-teal-400 transition-colors duration-200 group"
                                >
                                    <div className="w-6 h-6 md:w-8 md:h-8 bg-gray-800 rounded-lg flex items-center justify-center group-hover:bg-gradient-to-br group-hover:from-teal-400 group-hover:to-teal-500 transition-colors duration-200 border border-gray-700">
                                        {contact.icon}
                                    </div>
                                    <span className="text-xs md:text-sm">{contact.text}</span>
                                </a>
                            ))}
                        </div>
                    </motion.div>
                </div>

                {/* Stats Section */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.4 }}
                    className="mt-12 md:mt-16 pt-6 md:pt-8 border-t border-gray-700"
                >
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                        {statsData.map((stat, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, scale: 0.8 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.4, delay: index * 0.1 }}
                                className="text-center"
                            >
                                <div className={`w-10 h-10 md:w-12 md:h-12 ${index % 2 === 0 ? 'bg-gradient-to-br from-orange-400 to-orange-500' : 'bg-gradient-to-br from-teal-400 to-teal-500'} rounded-xl flex items-center justify-center mx-auto mb-2 md:mb-3 shadow-lg`}>
                                    {stat.icon}
                                </div>
                                <div className="text-lg md:text-2xl font-bold text-white mb-1">
                                    {stat.loading ? (
                                        <div className="h-6 md:h-8 bg-gray-700 rounded animate-pulse"></div>
                                    ) : (
                                        stat.value
                                    )}
                                </div>
                                <div className="text-xs md:text-sm text-gray-300">{stat.label}</div>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>
            </div>

            {/* Bottom Bar */}
            <div className="bg-slate-950 border-t border-gray-700">
                <div className="container mx-auto px-4 py-4 md:py-6">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-3 md:gap-4">
                        <div className="flex flex-col md:flex-row items-center gap-2 md:gap-4 text-xs md:text-sm text-gray-400">

                            <p className='p-0 m-0'>&copy; {new Date().getFullYear()} EventPlots. All rights reserved.</p>

                            <div className="flex items-center gap-3 md:gap-4">
                                <Link to="/privacy" className="hover:text-orange-400 transition-colors duration-200">
                                    Privacy Policy
                                </Link>
                                <Link to="/terms" className="hover:text-orange-400 transition-colors duration-200">
                                    Terms & Conditions
                                </Link>
                            </div>
                        </div>
                        <div className="flex items-center gap-1 md:gap-2 text-xs md:text-sm text-gray-400">
                            <span>Made with</span>
                            <Heart className="w-3 h-3 md:w-4 md:h-4 text-red-400 fill-current" />
                            <span>by</span>
                            <span className="text-teal-400 font-medium">Graphite IT</span>
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
